const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://chezzle-api.fly.dev"
    : "http://localhost:3001";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");
const WS_BASE_URL = (
  process.env.REACT_APP_WS_URL || API_BASE_URL.replace(/^http/i, "ws")
).replace(/\/$/, "");
const WEBSOCKET_URL = `${WS_BASE_URL}/stockfish-personalised`;

class StockfishSocketService {
  constructor() {
    this.socket = null;
    this.subscribers = new Set();
    this.connectionPromise = null;
    this.isConnected = false;
    this.isGeneratingPuzzles = false;
    this.analysisRunForSession = false;
  }

  connect() {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      console.log("[SocketService] Initializing new WebSocket connection...");
      this.socket = new WebSocket(WEBSOCKET_URL);

      this.socket.onopen = () => {
        console.log("[SocketService] WebSocket connection established.");
        this.isConnected = true;
        this.subscribers.forEach((sub) => sub.onConnect?.());
        resolve();
      };

      this.socket.onmessage = this.onmessage.bind(this);

      this.socket.onerror = (error) => {
        console.error("[SocketService] WebSocket error:", error);
        this.isConnected = false;
        this.subscribers.forEach((sub) => {
          sub.onDisconnect?.();
        });
        reject(error);
        this.connectionPromise = null;
      };

      this.socket.onclose = (event) => {
        console.log(
          `[SocketService] WebSocket connection closed. Code: ${event.code}`
        );
        this.isConnected = false;
        this.isGeneratingPuzzles = false;
        this.socket = null;
        this.connectionPromise = null;
        this.subscribers.forEach((sub) => {
          sub.onDisconnect?.();
          sub.onGenerationStatusChange?.(false);
        });
      };
    });

    return this.connectionPromise;
  }

  subscribe(subscriber) {
    this.connect().catch((e) => console.warn("Initial connection failed", e));
    this.subscribers.add(subscriber);

    if (this.isConnected) {
      subscriber.onConnect?.();
    }
    subscriber.onGenerationStatusChange?.(this.isGeneratingPuzzles);
    subscriber.onSessionStatusChange?.(this.analysisRunForSession);
  }

  unsubscribe(subscriber) {
    this.subscribers.delete(subscriber);
  }

  send(type, payload) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      if (type === "start_puzzle_generation") {
        if (this.isGeneratingPuzzles || this.analysisRunForSession) {
          console.warn(
            "[SocketService] Ignoring 'start_puzzle_generation' command (already run or in progress)."
          );
          return;
        }
        this.isGeneratingPuzzles = true;
        this.analysisRunForSession = true;

        this.subscribers.forEach((sub) => {
          sub.onGenerationStatusChange?.(true);
          sub.onSessionStatusChange?.(true);
        });
      }

      this.socket.send(JSON.stringify({ type, ...payload }));
    } else {
      console.warn("[SocketService] Cannot send command, socket is not open.", {
        type,
        payload,
      });
    }
  }

  onmessage(event) {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (e) {
      console.error("Failed to parse message", event.data);
      return;
    }

    if (message.type === "puzzle_generation_complete") {
      this.isGeneratingPuzzles = false;
      this.subscribers.forEach((sub) => sub.onGenerationStatusChange?.(false));
    } else if (message.type === "error") {
      const msgText = message.message || "";
      const isManualStop = msgText.includes("Live analysis manually stopped");

      const isAlreadyRunning =
        msgText.includes("busy") || msgText.includes("Already claimed");

      if (isAlreadyRunning) {
        console.log(
          "[SocketService] Detected active generation session on backend. Syncing state to ACTIVE."
        );
        if (!this.isGeneratingPuzzles) {
          this.isGeneratingPuzzles = true;
          this.subscribers.forEach((sub) =>
            sub.onGenerationStatusChange?.(true)
          );
        }
        return;
      }

      if (!isManualStop) {
        console.warn("[SocketService] Received error from backend:", message);
      }
    }

    this.subscribers.forEach((sub) => sub.onMessage?.(message));
  }

  setGenerationActive(isActive) {
    if (this.isGeneratingPuzzles !== isActive) {
      console.log(
        `[SocketService] Manually setting generation active to: ${isActive}`
      );
      this.isGeneratingPuzzles = isActive;
      this.subscribers.forEach((sub) =>
        sub.onGenerationStatusChange?.(isActive)
      );
    }
  }
}

export const stockfishSocketService = new StockfishSocketService();
